import {useState, useRef, useEffect} from 'react';
import Plyr from 'plyr';
import {IVideoUrl} from '@dazn/lp-web/src/types';
import {DaznUtils, encodeURL} from '@dazn/lp-web/src/utils';

const {isIOS} = DaznUtils.Platform;

export const useVideoPlayer = (
    videoUrl: IVideoUrl,
    index: number,
    customPlayerId?: string
) => {
    const [shouldShowThumbnail, setShowThumbnail] = useState(true);
    const playerId = useRef(customPlayerId || `plyr-player-${index}`);
    const playerRef = useRef<Plyr | null>(null);
    const videoContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        initializePlayer();

        return () => {
            destroyPlayer();
        };
    }, [videoUrl]);

    useEffect(() => {
        if (playerRef.current) {
            playerRef.current.on('playing', hideThumbnail);
        }
    }, [playerRef.current]);

    const initializePlayer = () => {
        const options = {
            controls: [
                'play-large',
                'play',
                'progress',
                'current-time',
                'mute',
                'volume'
            ],
            settings: ['captions', 'quality', 'loop'],
            speed: {selected: 1, options: [1]},
            hideControls: false,
            clickToPlay: false,
            fullscreen: {enabled: false}
        };

        playerRef.current = new Plyr(`#${playerId.current}`, options);
        playerRef.current.on('ended', rewind);
        playerRef.current.on('playing', hideThumbnail);
        playerRef.current.on('ready', handleMountTracking);
    };

    const handleMountTracking = () => {
        const {current} = playerRef;

        if (current) {
            const elements = current.elements as {
                buttons: {play: HTMLButtonElement[]};
                controls: HTMLElement;
            };

            elements.buttons.play[0].addEventListener(
                'click',
                handleControlsTogglePlay
            );

            const controls = elements.controls as HTMLElement;
            controls.children[3].children[0].addEventListener(
                'click',
                handleControlsToggleMute
            );
        }
    };

    const destroyPlayer = () => {
        if (playerRef.current) {
            playerRef.current.off('ended', rewind);
            playerRef.current.off('playing', hideThumbnail);
            playerRef.current.off('ready', handleMountTracking);
            playerRef.current.destroy();
        }
    };

    const rewind = () => {
        if (playerRef.current) {
            playerRef.current.stop();
            showThumbnail();
        }
    };

    const hideThumbnail = () => setShowThumbnail(false);
    const showThumbnail = () => setShowThumbnail(true);

    const handleControlsTogglePlay = () => {};

    const handleControlsToggleMute = () => {};

    const enterFullScreen = () => {
        if (playerRef.current && videoUrl.provider === 'thumbnail') {
            playerRef.current.poster = encodeURL(videoUrl.src);
        }
    };

    const exitFullScreen = () => {};

    const toggleFullScreen = () => {
        if (isIOS(window)) {
            if (
                videoContainerRef.current?.classList.contains(
                    'fullscreen-fallback'
                )
            ) {
                videoContainerRef.current.classList.remove(
                    'fullscreen-fallback'
                );
                exitFullScreen();
            } else {
                videoContainerRef.current?.classList.add('fullscreen-fallback');
                enterFullScreen();
            }
        } else if (window.document.fullscreenElement) {
            window.document.exitFullscreen();
            exitFullScreen();
        } else {
            videoContainerRef.current?.requestFullscreen();
            enterFullScreen();
        }
    };

    return {
        shouldShowThumbnail,
        playerId: playerId.current,
        videoContainerRef,
        toggleFullScreen,
        togglePlayerState: () => {
            if (playerRef.current) {
                playerRef.current.togglePlay();
            }
        }
    };
};
